import { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, Progress, Skeleton, Tag } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import intl from "react-intl-universal";
import { getDefaultModelQuota } from "@/apis/defaultModelQuota";
import styles from "./defaultModelQuota.module.scss";

const t = (key) => intl.get(`DefaultModelQuota.${key}`);
const number = (value) => new Intl.NumberFormat().format(value);

// Use the server's accounting month, independent of the browser's local timezone.
const resetDate = (period) => {
  const [year, month] = period.split("-").map(Number);
  return `${month === 12 ? year + 1 : year}-${String(month === 12 ? 1 : month + 1).padStart(2, "0")}-01 00:00`;
};

export default function DefaultModelQuota() {
  const userUuid = useSelector((state) => state.user.userInfo.uuid);
  const [quota, setQuota] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const requestVersion = useRef({ version: 0 });

  const load = useCallback(async () => {
    const version = ++requestVersion.current.version;
    setLoading(true);
    setFailed(false);
    // Hide an old snapshot while refreshing; an error must not appear as zero usage.
    setQuota(null);
    try {
      const response = await getDefaultModelQuota();
      const data = response.data;
      if (!data || !/^\d{4}-(0[1-9]|1[0-2])$/.test(data.period)
          || ![data.tokenLimit, data.usedTokens, data.remainingTokens].every((value) => typeof value === "number" && Number.isFinite(value) && value >= 0)
          || typeof data.allowed !== "boolean") {
        throw new Error("Invalid quota response");
      }
      if (version === requestVersion.current.version) setQuota(data);
    } catch {
      if (version === requestVersion.current.version) setFailed(true);
    } finally {
      if (version === requestVersion.current.version) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const requests = requestVersion.current;
    load();
    // Refresh when returning from OpenGA or another browser tab.
    window.addEventListener("focus", load);
    return () => {
      ++requests.version;
      window.removeEventListener("focus", load);
    };
  }, [load, userUuid]);

  const percent = quota?.tokenLimit > 0 ? Math.min(100, quota.usedTokens / quota.tokenLimit * 100) : 0;
  const status = quota?.tokenLimit === 0 ? "Disabled" : quota?.allowed ? "Available" : "Exhausted";

  return <section className={styles.card} aria-label={t("Title")}>
    <div className={styles.header}>
      <strong>{t("Title")}</strong>
      <Button type="text" size="small" aria-label={t("Refresh")} disabled={loading} icon={<ReloadOutlined spin={loading} />} onClick={load} />
    </div>
    {loading ? <Skeleton active title={false} paragraph={{ rows: 3 }} /> : failed ? (
      <Alert type="error" showIcon title={t("Load failed")} action={<Button type="link" size="small" onClick={load}>{t("Retry")}</Button>} />
    ) : quota && <>
      <div className={styles.status}>
        <span>{quota.period}</span>
        <Tag color={quota.allowed ? "green" : "orange"}>{t(status)}</Tag>
      </div>
      <div className={styles.usage}>
        <span>{t("Used")}</span>
        <span><strong>{number(quota.usedTokens)}</strong> / {number(quota.tokenLimit)} token</span>
      </div>
      <Progress aria-label={t("Usage progress")} aria-valuenow={Math.round(percent)} percent={percent} showInfo={false} status={quota.allowed ? "normal" : "exception"} strokeColor={quota.allowed ? "#d62293" : undefined} />
      <div className={styles.usage}><span>{t("Remaining")}</span><strong>{number(quota.remainingTokens)} token</strong></div>
      <p className={styles.note}>{t("Reset")} {resetDate(quota.period)} {t("Beijing time")}</p>
      {!quota.allowed && <Alert type="warning" showIcon title={t(quota.tokenLimit === 0 ? "Disabled hint" : "Exhausted hint")} />}
      <p className={styles.note}>{t("Reporting hint")}</p>
    </>}
  </section>;
}
